import { useMemo, useState } from "react";
import { PortfolioResponse, PortfolioOrder, PortfolioPosition } from "../types/portfolio";
import { PortfolioHeader } from "./portfolio/PortfolioHeader";
import { PositionsSection } from "./portfolio/PositionsSection";
import { OpenOrdersSection } from "./portfolio/OpenOrdersSection";
import { RecentTradesSection } from "./portfolio/RecentTradesSection";
import {
  computeMetrics,
  groupOrders,
  numberFormatter,
  quantityFormatter,
  entryPriceFormatter,
} from "./portfolio/portfolioUtils";
import { StatusMessage, CollapsibleSection, SegmentedFilter } from "./ui";
import { cn } from "../lib/utils";

type PortfolioViewProps = {
  data: PortfolioResponse | null;
  isLoading: boolean;
  isError: boolean;
  competitionCode: string;
  onCancelOrder?: (orderId: number) => Promise<void>;
  onEditOrder?: (order: PortfolioOrder) => void;
  onPromoteOrder?: (order: PortfolioOrder) => void;
};

type FilterPillOption<T extends string> = {
  label: string;
  value: T;
  activeTone?: "accent" | "buy" | "sell";
};

function isFlatPortfolioPosition(position: PortfolioPosition) {
  return position.quantity === 0 || position.status === "FLAT";
}

function FilterPills<T extends string>({
  value,
  onChange,
  options,
  className,
  size = "compact",
}: {
  value: T;
  onChange: (value: T) => void;
  options: FilterPillOption<T>[];
  className?: string;
  size?: "compact" | "section";
}) {
  return (
    <SegmentedFilter
      value={value}
      onChange={onChange}
      options={options}
      size={size === "section" ? "regular" : "compact"}
      ariaLabel="Portfolio filter"
      className={cn(
        "portfolioSubfilter",
        size === "section"
          ? "w-full md:w-auto md:min-w-[420px]"
          : "w-full md:w-auto md:min-w-[260px]",
        className,
      )}
      chipClassName={size === "section" ? undefined : "min-w-[4.5rem] px-3"}
    />
  );
}

export default function PortfolioView({
  data,
  isLoading,
  isError,
  competitionCode,
  onCancelOrder,
  onEditOrder,
  onPromoteOrder,
}: PortfolioViewProps) {
  const [cancellingOrderId, setCancellingOrderId] = useState<number | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [sectionFilter, setSectionFilter] = useState<"ALL" | "POSITIONS" | "ORDERS" | "HISTORY">("ALL");
  const [positionFilter, setPositionFilter] = useState<"ALL" | "LONG" | "SHORT">("ALL");
  const [openOrderFilter, setOpenOrderFilter] = useState<"ALL" | "BUY" | "SELL">("ALL");
  const [historyFilter, setHistoryFilter] = useState<"ALL" | "BUY" | "SELL" | "SETTLE">("ALL");

  const metrics = useMemo(() => computeMetrics(data), [data]);
  const groupedOrders = useMemo(() => groupOrders(data?.orders ?? []), [data?.orders]);
  const filteredPositions = useMemo(() => {
    const positions = data?.positions ?? [];
    if (positionFilter === "ALL") return positions;
    if (positionFilter === "LONG") {
      return positions.filter((position) => !isFlatPortfolioPosition(position) && position.quantity > 0);
    }
    if (positionFilter === "SHORT") {
      return positions.filter((position) => !isFlatPortfolioPosition(position) && position.quantity < 0);
    }
    return positions;
  }, [data?.positions, positionFilter]);
  const filteredOpenOrders = useMemo(() => {
    const orders = groupedOrders.openOrders;
    if (openOrderFilter === "ALL") return orders;
    return orders.filter((order) => order.side === openOrderFilter);
  }, [groupedOrders.openOrders, openOrderFilter]);
  const filteredTrades = useMemo(() => {
    const trades = data?.trades ?? [];
    if (historyFilter === "ALL") return trades;
    if (historyFilter === "SETTLE") {
      return trades.filter((trade) => trade.trade_type === "SETTLEMENT");
    }
    return trades.filter(
      (trade) => trade.trade_type !== "SETTLEMENT" && trade.side === historyFilter,
    );
  }, [data?.trades, historyFilter]);

  const handleCancelOrder = async (orderId: number) => {
    if (!onCancelOrder) return;

    try {
      setCancelError(null);
      setCancellingOrderId(orderId);
      await onCancelOrder(orderId);
    } catch (error) {
      console.error("Failed to cancel order", error);
      setCancelError(error instanceof Error ? error.message : "Failed to cancel order.");
    } finally {
      setCancellingOrderId(null);
    }
  };

  if (isLoading) {
    return (
      <section className="rounded-card border border-border-subtle bg-background-card p-3">
        <StatusMessage>Loading portfolio.</StatusMessage>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="rounded-card border border-border-subtle bg-background-card p-3">
        <StatusMessage variant="error">Unable to load portfolio. Try refreshing.</StatusMessage>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="rounded-card border border-border-subtle bg-background-card p-3">
        <StatusMessage>Select a pool to view your portfolio.</StatusMessage>
      </section>
    );
  }

  const standing = data.standing;
  // Use backend-calculated unrealized P&L (single source of truth)
  const unrealizedPnL = data.unrealized_pnl || 0;
  const realizedPnL = data.realized_pnl || 0;
  const totalPnL = unrealizedPnL + realizedPnL;
  const showPositions = sectionFilter === "ALL" || sectionFilter === "POSITIONS";
  const showOrders = sectionFilter === "ALL" || sectionFilter === "ORDERS";
  const showHistory = sectionFilter === "ALL" || sectionFilter === "HISTORY";

  const positionsSection = showPositions ? (
    <CollapsibleSection
      title="Positions"
      isExpanded
      onToggle={() => {}}
      showToggle={false}
      boxed={false}
      compact
      headerAction={
        <FilterPills
          value={positionFilter}
          onChange={setPositionFilter}
          options={[
            { label: "All", value: "ALL" },
            { label: "Long", value: "LONG", activeTone: "buy" },
            { label: "Short", value: "SHORT", activeTone: "sell" },
          ]}
        />
      }
    >
      <PositionsSection
        positions={filteredPositions}
        trades={data.trades}
        competitionCode={competitionCode}
        quantityFormatter={quantityFormatter}
        numberFormatter={numberFormatter}
        entryPriceFormatter={entryPriceFormatter}
      />
    </CollapsibleSection>
  ) : null;

  const ordersSection = showOrders ? (
    <CollapsibleSection
      title="Open Orders"
      isExpanded
      onToggle={() => {}}
      showToggle={false}
      boxed={false}
      compact
      headerAction={
        <FilterPills
          value={openOrderFilter}
          onChange={setOpenOrderFilter}
          options={[
            { label: "All", value: "ALL" },
            { label: "Buy", value: "BUY", activeTone: "buy" },
            { label: "Sell", value: "SELL", activeTone: "sell" },
          ]}
        />
      }
    >
      <OpenOrdersSection
        orders={filteredOpenOrders}
        competitionCode={competitionCode}
        quantityFormatter={quantityFormatter}
        numberFormatter={numberFormatter}
        cancellingOrderId={cancellingOrderId}
        cancelError={cancelError}
        onCancelOrder={handleCancelOrder}
        hasOnCancelOrder={Boolean(onCancelOrder)}
        onPromoteOrder={onPromoteOrder}
        hasOnPromoteOrder={Boolean(onPromoteOrder)}
        onEditOrder={onEditOrder}
        hasOnEditOrder={Boolean(onEditOrder)}
      />
    </CollapsibleSection>
  ) : null;

  const historySection = showHistory ? (
    <CollapsibleSection
      title="History"
      isExpanded
      onToggle={() => {}}
      showToggle={false}
      boxed={false}
      compact
      headerAction={
        <FilterPills
          value={historyFilter}
          onChange={setHistoryFilter}
          options={[
            { label: "All", value: "ALL" },
            { label: "Buy", value: "BUY", activeTone: "buy" },
            { label: "Sell", value: "SELL", activeTone: "sell" },
            { label: "Settle", value: "SETTLE" },
          ]}
        />
      }
    >
      <RecentTradesSection
        trades={filteredTrades}
        competitionCode={competitionCode}
        quantityFormatter={quantityFormatter}
        numberFormatter={numberFormatter}
      />
    </CollapsibleSection>
  ) : null;

  return (
    <section className="space-y-3">
      <PortfolioHeader
        rank={standing.rank ?? 0}
        participantCount={standing.participant_count}
        pointsBalance={standing.points_balance ?? 0}
        deltaWithLeader={metrics.deltaWithLeader}
        leaderPointsBalance={standing.leader_points_balance ?? 0}
        totalPnL={totalPnL}
        unrealizedPnL={unrealizedPnL}
        realizedPnL={realizedPnL}
        numberFormatter={numberFormatter}
      />

      <div className="px-1">
        <FilterPills
          value={sectionFilter}
          onChange={setSectionFilter}
          size="section"
          className="w-full md:w-auto"
          options={[
            { label: "All", value: "ALL" },
            { label: "Positions", value: "POSITIONS" },
            { label: "Orders", value: "ORDERS" },
            { label: "History", value: "HISTORY" },
          ]}
        />
      </div>

      {positionsSection}

      {ordersSection}
      {historySection}
    </section>
  );
}
